<template>
<div
  class="cart-modal"
  :class="{
    [`cart-modal--stage-${stage}`]: true,
  }"
>
  <header class="cart-modal-header">
    <button
      v-show="backButtonAction"
      @click="() => { if (backButtonAction) backButtonAction(); }"
      class="cart-modal__back-button"
    >
      <img :src="require('@/assets/images/icons/icon-arrow-2-left.svg')" alt="back">
    </button>
    <h2 class="cart-modal-title">{{ stageTitle }}</h2>
    <button class="close-cart-modal-btn" @click="$emit('dismiss')">
      <IconCross/>
    </button>
  </header>
  <section class="cart-modal-body-and-footer">
    <div class="cart-modal-body">
      <transition name="opacity">
        <keep-alive>
          <CartPreviewStage
            v-if="stage === 'preview'"
          />
          <OrderAuthenticationStage
            v-else-if="stage === 'authentication'"
          />
          <OrderSubmissionStage
            v-else-if="stage === 'submission'"
            :submitOrderApiError="submitOrderApiError"
            @updated="submitOrderApiData = $event"
            ref="orderSubmissionStage"
          />
          <OrderConfirmationStage
            v-else-if="stage === 'confirmation'"
            :phone="submitOrderApiData.phone"
            :order-id="order.id"
            @completed="() => { stage = 'submitted'; submissionCompleted(); }"
          />
          <ThankYouStage v-else-if="stage === 'submitted'"
                         :key="`thank-you-${onlinePayment.orderId}`"
                         :online-payment-required="onlinePayment.required"
                         :order-id="onlinePayment.orderId"
                         :order-token="onlinePayment.orderToken"
          />
        </keep-alive>
      </transition>
    </div>
    <footer v-if="shouldDisplayFooter" class="cart-modal-footer">
      <div class="cart-modal-buttons">
        <button v-if="stage === 'preview'"
                type="button"
                class="btn btn-primary btn-rounded"
                :disabled="formIsBusy || promotionsAreChecking || checkoutUrlIsGenerating"
                @click="switchToSubmissionStage"
                key="next"
        >
          <i class="fal fa-spinner fa-spin" v-if="formIsBusy || promotionsAreChecking || checkoutUrlIsGenerating"></i>&nbsp;
          <template v-if="promotionsAreChecking || checkoutUrlIsGenerating">Пожалуйста, подождите...</template>
          <template v-else>Перейти к оформлению</template>
        </button>
        <template v-else-if="stage === 'submission'">
          <button type="button"
                  class="btn btn-primary btn-rounded"
                  :disabled="formIsBusy || promotionsAreChecking"
                  @click="dispatchOrderSubmission"
                  key="next"
          >
            <i class="fal fa-spinner fa-spin" v-if="formIsBusy || promotionsAreChecking"></i>&nbsp;
            <template v-if="promotionsAreChecking">Пожалуйста, подождите...</template>
            <template v-else>Оформить заказ</template>
          </button>
        </template>
      </div>
    </footer>
  </section>
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import asyncComponentWithLoading from '@/helpers/asyncComponentWithLoading'
import debounce from 'lodash/debounce'
import zenkyEvents from '@/events'
import storage from '@/storage'

export default {
  components: {
    CartPreviewStage: asyncComponentWithLoading(() => import(/* webpackPreload: true */ './stages/CartPreviewStage')),
    OrderAuthenticationStage: asyncComponentWithLoading(() => import('./stages/OrderAuthenticationStage')),
    OrderSubmissionStage: asyncComponentWithLoading(() => import('./stages/OrderSubmissionStage')),
    OrderConfirmationStage: asyncComponentWithLoading(() => import('./stages/OrderConfirmationStage')),
    ThankYouStage: asyncComponentWithLoading(() => import('./stages/ThankYouStage')),
    IconCross: () => import(/* webpackPreload: true */ '@/assets/images/icons/icon-cross.svg?inline'),
  },

  created() {
    this.$root.$on('cartModalIsClosing', () => {
      // 400ms, so it doesn't reset while the modal is being closed. TODO close it with `v-if` and transitions.
      setTimeout(() => {
        if (this.stage === 'submitted') {
          this.stage = 'preview';
        }
      }, 400)
    });

    let orderPaymentsPreviewIsLoading = false

    const loadOrderRelevantInfo = orderId => {
      if (!orderId) {
        return;
      } else if (orderPaymentsPreviewIsLoading) {
        return
      }

      orderPaymentsPreviewIsLoading = true

      this.$store.dispatch('order/loadPaymentsPreview')
        .then(() => orderPaymentsPreviewIsLoading = false)
        .catch(() => orderPaymentsPreviewIsLoading = false)
    }

    this.$watch(
      () => this.$store.state.order.order,
      () => loadOrderRelevantInfo(this.$store.state.order.order?.id),
      { deep: true, immediate: true },
    );

    const debounceReactToDraftPaymentsChange = debounce(loadOrderRelevantInfo, 300);

    this.$watch(
      () => this.$store.state.order.draftPayments,
      () => debounceReactToDraftPaymentsChange(this.$store.state.order.order?.id),
      { deep: true, immediate: true },
    );

    this.$watch(
      () => this.$store.state.auth.user?.id,
      () => loadOrderRelevantInfo(this.$store.state.order.order?.id),
      { deep: true, immediate: true },
    );
  },

  computed: {
    ...mapGetters({
      order: 'order/order',
      hasVariants: 'order/hasVariants',
      totalPrice: 'order/totalPrice',
      hasUser: 'auth/hasUser',
      authenticationEnabled: 'store/authenticationEnabled',
      authenticationMethod: 'store/orderAuthenticationMethod',
      promotionsAreChecking: 'order/promotionsAreChecking',
      checkoutUrlIsGenerating: 'order/checkoutUrlIsGenerating',
      ordersSettings: 'settings/orders',
      tabId: 'ui/tabId',
    }),

    shouldDisplayFooter() {
      return this.hasVariants && !['submitted', 'authentication'].includes(this.stage)
    },

    stageTitle() {
      switch (this.stage) {
        case 'preview': return 'Корзина';
        case 'authentication': return 'Оформление заказа';
        case 'submission': return 'Оформление заказа';
        case 'confirmation': return 'Подтверждение заказа';
        case 'submitted': return 'Заказ принят';
      }
    },

    backButtonAction() {
      switch (this.stage) {
        case 'preview': return null;
        case 'authentication': return () => this.stage = 'preview';
        case 'submission': return () => this.stage = 'preview';
        case 'confirmation': return () => this.stage = 'submission';
        case 'submitted': return null;
      }
    },

    firstOnlinePayment() {
      if (!this.submitOrderApiData) {
        return null;
      } else if (!Array.isArray(this.submitOrderApiData.payments)) {
        return null;
      }

      const index = this.submitOrderApiData
        .payments
        .findIndex(payment => this.onlinePayment.methods.indexOf(payment.method) !== -1);

      if (index === -1) {
        return null;
      }

      return this.submitOrderApiData.payments[index];
    },

    onlinePaymentSelected() {
      return this.firstOnlinePayment !== null;
    },
  },

  methods: {
    ...mapActions({
      submitOrder: 'order/submitOrder',
      updateOrder: 'order/updateOrder',
      recreateOrder: 'order/recreateOrder',
      getReCaptchaToken: 'recaptcha/getToken',
      createPaymentTransaction: 'order/createPaymentTransaction',
      generateCheckoutUrl: 'order/generateCheckoutUrl',
    }),

    submissionCompleted() {
      this.$nextTick(() => {
        this.submitOrderApiData = null;
        this.recreateOrder()
      })
    },

    switchToSubmissionStage() {
      if (this.formIsBusy || this.promotionsAreChecking || this.checkoutUrlIsGenerating) {
        return
      } else if (this.ordersSettings.checkout_enabled) {
        return this.redirectToCheckoutPage();
      } else if (this.authenticationEnabled && this.authenticationMethod === 'preauth' && !this.hasUser) {
        this.stage = 'authentication'
      } else {
        this.stage = 'submission'
      }

      this.$root.sharedEventBus.$emit(zenkyEvents.ORDER_SUBMISSION_STARTED, {
        order: this.order,
      })
    },

    async redirectToCheckoutPage() {
      if (this.checkoutUrlIsGenerating) {
        return;
      }

      try {
        const url = await this.generateCheckoutUrl();

        if (url === null) {
          alert('Не удалось перейти к оформлению заказа.');

          return;
        }

        storage.submittedFromTabId(this.tabId);

        location.href = url;
      } catch (e) {
        console.error(e);
        alert('Не удалось перейти к оформлению заказа.');
      }
    },

    orderConfirmationRequired(response) {
      return response.result.phone_confirmation_required
    },

    async validateOrderForm() {
      const validationObserver = this.$refs.orderSubmissionStage.$refs.validationObserver;

      if (await validationObserver.validate() === false) {
        // Focus the first invalid input, using non-starndard vee-validate API
        let elToScrollEmphasize;
        await new Promise(resolve => setTimeout(resolve, 100)); // So the errors object has time to update.
        try {
          const [firstFieldWithErrorsName, ] = Object.entries(validationObserver.errors)
            .find(([fieldName, fieldErrors]) => fieldErrors.length > 0)
          elToScrollEmphasize = validationObserver.refs[firstFieldWithErrorsName].$el;
        } catch (err) {
          elToScrollEmphasize = validationObserver.$el;
        }
        const undelyingInput = elToScrollEmphasize.querySelector('input'); // TODO not only `input`?
        if (undelyingInput) undelyingInput.focus({ preventScroll: true });
        // In Chrome 79.0.3945.130 `focus({ preventScroll: true })` with immediate `scrollIntoView` doesn't
        // perform the scroll at all. `setTimeout` solves it. Low values don't work sometimes.
        setTimeout(() => elToScrollEmphasize.scrollIntoView({ behavior: 'smooth' }), 10);

        return false;
      }

      return true;
    },

    async dispatchOrderSubmission(withData = true) {
      if (this.formIsBusy || this.promotionsAreChecking) {
        return
      } else if (await this.validateOrderForm() === false) {
        return;
      }

      this.formIsBusy = true
      this.submitOrderApiError = null

      this.getReCaptchaToken('order_submission').then(token => {
        const data = withData ? { ...this.submitOrderApiData } : {};

        if (token) {
          data.recaptcha = { token };
        }

        this.onlinePayment.orderId = this.order.id
        this.onlinePayment.orderToken = this.order.token
        this.onlinePayment.required = this.submitOrderApiData.payments
          .findIndex(payment => payment.method === 'cloudpayments') !== -1

        storage.submittedFromTabId(this.tabId);

        this.submitOrder(data)
          .then(data => {
            this.$root.sharedEventBus.$emit(zenkyEvents.ORDER_SUBMITTED, {
              order: data.order,
              variants: this.order.variants,
            })

            if (this.orderConfirmationRequired(data)) {
              this.stage = 'confirmation';
            } else {
              this.stage = 'submitted';
              this.submissionCompleted()
            }

            storage.deliveryMethod(null, true)
          })
          .catch(err => {
            console.error(err)
            this.showSubmitError(err)
            storage.submittedFromTabId(null, true);
          })
          .finally(() => this.formIsBusy = false)
      })
    },

    showSubmitError(err) {
      const resData = err.response?.data;

      if (resData && resData.error && resData.error.error_code) {
        return this.submitOrderApiError = resData.error
      }

      if (resData?.error?.error_code === 'orders.submission.disabled') {
        return alert('Приём заказов на данный момент недоступен.');
      } else if (resData?.errors?.phone) {
        return alert('Ошибка: необходимо указать корректный номер телефона.');
      }

      return alert('Не удалось оформить заказ.');
    },
  },

  watch: {
    hasUser: [
      function(n, o) {
        if (this.stage === 'authentication') {
          if (n === true) {
            this.stage = 'submission';
          }
        }
      },
    ],
  },

  data() {
    return {
      stage: 'preview',

      formIsBusy: false,
      submitOrderApiData: null,
      submitOrderApiError: null,

      onlinePayment: {
        required: false,
        orderId: '',
        orderToken: '',
        methods: ['cloudpayments'],
      },
    }
  }
}
</script>

<style lang="less">
@import '~@/assets/less/components/utilities';

.cart-modal {
  @apply h-full w-full flex flex-col;
  background-color: #F4F4F0;

  header.cart-modal-header {
    @apply flex items-center bg-primary px-4 text-white leading-none
      flex-shrink-0; // Otherwise it gets compressed on mobile.
    height: var(--header-height);


    .cart-modal__back-button {
      @apply p-2;
      img {
        // So the layout doesn't jump while it is loading.
        width: image-width("src/assets/images/icons/icon-arrow-2-left.svg");
        height: image-height("src/assets/images/icons/icon-arrow-2-left.svg")
      }
    }
    .cart-modal-title {
      @apply mx-2 flex-grow text-2xl font-normal leading-normal;
    }
    .close-cart-modal-btn {
      @apply p-1;
      svg {
        @apply fill-current text-white block;
        width: calc(theme('width.4') + 2px);;
      }
    }
  }

  .cart-modal-body-and-footer {
    @apply flex-grow overflow-y-auto flex flex-col;

    .cart-modal-body {
      @apply flex-grow
        relative; // For transitions.

      .order-summary {
        @apply bg-white p-4 my-4 rounded;
        table {
          @apply w-full;
          tr {
            @apply leading-normal;
            td {
              &:last-child {
                @apply text-right;
              }
            }
          }
        }
        .order-summary__to-pay {
          @apply text-xl leading-none;
          td {
            @apply pt-1;
          }
          td:last-child {
            @apply text-2xl font-bold;
          }
        }
      }

      .cart-modal-preview-stage__products-list-and-summary {
        @apply px-4
          w-full; // So it doesn't shrink when we apply the transition.

        .cart-modal-products-list {
          @apply w-full;

          .card-modal-products-list-item {
            @apply w-full my-4 flex items-center bg-white rounded-lg py-2;
            transition: box-shadow 0.1s;

            &:hover {
              @apply shadow;
            }

            .cart-product-image-wrapper {
              @apply w-20 mr-2;
              .product-image {
                a {
                  .aspect-ratio-image(1);
                  img {
                    @apply rounded-l-lg object-cover;
                  }
                }
              }
            }

            .cart-product-info {
              @apply mx-1 flex-grow;

              a {
                @apply text-black;
                > * {
                  @apply my-1;
                  &::after {
                    content: "\A";
                    @apply whitespace-pre;
                  }
                }
              }

              .cart-product-title {
                @apply text-sm font-normal;
              }

              .product-variant-description {
                @apply text-xs font-light opacity-60;
              }

              .product-variant-modifiers-list {
                @apply text-xs font-light opacity-60;
              }

              .cart-product-price {
                @apply text-primary text-xs font-light;
              }
            }

            .cart-product-quantity-controls-wrapper {
              @apply self-stretch text-center flex-grow flex justify-end;
              order: 10;

              &.cart-modal-quantity-controls {
                @apply flex-grow-0 order-none;
              }

              .product-quantity-controls-component {
                @apply mt-0 w-auto bg-light-lighter rounded-r-lg rounded-l-none;

                .product-quantity-controls-wrapper {
                  @apply border border-light-dark flex-row;
                  transition: border-color 0.1s;

                  &:hover {
                    @apply border-primary;
                  }

                  .product-quantity-control-column {
                    @apply w-8;

                    input {
                      @apply text-sm font-medium text-dark;
                    }

                    button {
                      @apply p-0;
                      i {
                        @apply text-primary;
                      }
                    }
                  }
                }
              }
            }

            .cart-product-total-price-wrapper {
              @apply text-right;
            }

            .cart-product-actions-wrapper {
              @apply text-center;

              .cart-product-remove-product-btn {
                @apply text-light-dark;

                &:hover {
                  @apply text-primary;
                }

                a {
                  @apply text-black;
                }

                i {
                  @apply font-bold;
                }
              }
            }
          }
        }
      }

      .order-confirmation-stage {
        .order-confirmation-stage__code {
          @apply my-2;
        }
      }

      .opacity-leave-active {
        position: absolute;
      }
    }

    .cart-modal-footer {
      @apply border-t border-dark-lightest;

      .cart-modal-buttons {
        @apply w-full p-1 flex bg-white;
        > * {
          @apply h-12 flex-grow p-0 m-1;
        }
      }
    }
  }

}

.cart-modal-wrapper {
  .modal-view {
    .modal-container {
      width: 650px;
    }
  }
}

@screen sm {
  .cart-modal {
    .cart-modal-body-and-footer {
      .cart-modal-body {
        .cart-modal-preview-stage__products-list-and-summary {
          .cart-modal-products-list {
            .card-modal-products-list-item {
              .cart-product-quantity-controls-wrapper {
                @apply self-center;
                order: unset;
                .product-quantity-controls-component {
                  @apply bg-transparent;
                  .product-quantity-controls-wrapper {
                    @apply border rounded flex-row;
                    .product-quantity-control-column {
                      @apply h-6 w-6;
                    }
                  }
                }
              }
              .cart-product-actions-wrapper {
                @apply block;
              }
            }
          }
        }
      }
    }
  }
}

@screen lg {
  .cart-modal {
    .cart-modal-body-and-footer {
      .cart-modal-body {
        // So now the footer becomes fixed, i.e. is always at the bottom of the modal
        @apply overflow-y-auto;
      }
    }
  }
}
</style>
